<template>
  <form class="step" @submit.prevent="submit">
    <div v-if="allowChangingEvent && !item?.data" class="review-header">
      <IconReview class="mb-2" />
      <div class="text-xl font-semibold">
        {{ getTranslationByCategory('title') }}
      </div>
    </div>

    <div v-if="item?.data" class="review-event card md:mb-2.5">
      <div
        v-if="
          item?.data?.mainMedia?.data?.thumb ||
          eventGetters.getFirstThumb(item?.data)
        "
        class="review-event__image"
      >
        <img
          :src="
            item?.data?.mainMedia?.data?.thumb ??
            eventGetters.getFirstThumb(item?.data)
          "
          :alt="item?.data?.main_image?.alt"
        />
      </div>
      <div class="review-event__content">
        <div class="review-event__title">
          {{ item?.data?.event_name ?? item?.data?.title }}
        </div>
        <div v-if="item?.data?.date_attended" class="review-event__date">
          {{ formatDate(item?.data?.date_attended, locale) }}
        </div>
        <Button
          v-if="allowChangingEvent"
          variant="text"
          class="font-medium"
          @click.prevent="emit('click:select-event')"
        >
          {{
            item?.data
              ? $t('review.form.selectAnotherEvent')
              : $t('review.form.selectAnEvent')
          }}
        </Button>
        <div v-if="errors.event.isVisible" class="mt-2 text-sm text-red">
          {{ errors.event.message }}
        </div>
      </div>
    </div>

    <div class="review-form card md:mb-2.5">
      <div class="review-form__stars">
        <div class="label">{{ getTranslationByCategory('starsLabel') }}</div>
        <div class="stars" @mouseleave="hoverRating = 0">
          <div
            v-for="star in 5"
            :key="'star-' + star"
            class="star"
            @click="setRating(star)"
            @mouseover="hoverRating = star"
          >
            <IconStar
              class="icon"
              :class="{
                'text-gray-300': star > (hoverRating || localForm.rating),
              }"
            />
            <IconStarFull
              class="full"
              :class="{
                active: star <= (hoverRating || localForm.rating),
              }"
            />
          </div>
        </div>
        <div v-if="errors.rating.isVisible" class="mt-2 text-sm text-red">
          {{ errors.rating.message }}
        </div>
      </div>

      <div class="review-form__text">
        <div class="review-form__title">
          {{ getTranslationByCategory('textareaLabel') }}
        </div>
        <textarea
          v-model="localForm.review"
          :placeholder="getTranslationByCategory('textareaPlaceholder')"
          class="outline-stone-200"
          :class="errors.text.isVisible ? '!border-red' : ''"
          @update:modelValue="updateReview"
        />

        <div v-show="getTextForTextarea" class="review-form__notification">
          {{
            t('review.form.textareaText', {
              n: getTextForTextarea,
            })
          }}
        </div>

        <div v-if="errors.text.isVisible" class="text-sm text-red">
          {{ errors.text.message }}
        </div>
      </div>
    </div>

    <div class="review-footer card">
      <div class="review-footer__upload mb-4">
        <div class="review-form__title">{{ t('review.form.uploadLabel') }}</div>
        <div class="upload-section">
          <label
            v-if="uploadedFiles.length < 5"
            class="upload-section__item"
            :class="{ uploading: isUploading }"
          >
            <input
              type="file"
              multiple
              :accept="`${IMAGE_FILE_ACCEPT},video/mp4,video/quicktime,.mp4,.mov`"
              class="hidden"
              :disabled="uploadedFiles.length >= 5 || isUploading"
              @change="handleFileUpload"
            />
            <div v-if="!isUploading" class="icon">
              <IconPlus />
            </div>
            <div v-else class="loader"></div>
          </label>
          <div
            v-for="(file, index) in uploadedFiles"
            :key="index"
            class="upload-section__item upload-section__preview"
          >
            <img
              v-if="file.media_type === 'photo'"
              :src="getFileUrl(file)"
              alt="preview"
            />
            <video
              v-else-if="file.media_type === 'video'"
              :src="getFileUrl(file)"
            />
            <button class="upload-section__remove" @click="removeFile(index)">
              ×
            </button>
          </div>
        </div>
        <div class="review-form__notification">
          {{
            t('review.form.uploadText', {
              max: 5,
            })
          }}
        </div>
      </div>
      <div class="review-footer__submit">
        <Button
          color="primary"
          class="w-full"
          type="submit"
          :disabled="isUploading"
        >
          {{ t('review.form.submitButton') }}
        </Button>
      </div>
      <div class="review-form__legal">
        <i18n-t keypath="review.legalCopy">
          <template #termsAndConditions>
            <a :href="localePath('/terms-and-conditions/')" target="_blank">
              {{ t('review.termsAndConditions') }}
            </a>
          </template>
        </i18n-t>
      </div>
    </div>
  </form>
</template>

<script setup lang="ts">
import { ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { toast, type ToastOptions } from 'vue3-toastify'
import IconReview from '~/assets/icons/review/icon-review.svg?component'
import IconStar from '~/assets/icons/review/icon-star.svg?component'
import IconStarFull from '~/assets/icons/review/icon-star-full.svg?component'
import IconPlus from '~/assets/icons/review/icon-plus.svg?component'
import { useFileUpload } from '~/composables/useFileUpload'
import { IMAGE_FILE_ACCEPT } from '~/utils/normalizeImageFile'
import type { GalleryItem } from '~/composables/types/event'

interface Props {
  reviewForm: {
    review: string
    rating: number
    media: Array<{
      file_name: string
      media_type: 'photo' | 'video'
    }>
  }
  item: {
    data: {
      event_name: string
      main_image: GalleryItem
      readable_date_attended: string
      specialization: string
      supplier_name: string
      date_attended: string
    }
  }
  allowChangingEvent: boolean
}

const getTextForTextarea = computed(() => {
  return props.reviewForm.review ? props.reviewForm.review.slice(0, 30) : ''
})

const props = defineProps<Props>()
const emit = defineEmits<{
  (e: 'update:reviewForm', value: Props['reviewForm']): void
  (e: 'submit' | 'click:select-event'): void
}>()

const $localePath = useLocalePath()
const localePath = $localePath
const { t, locale } = useI18n()
const hoverRating = ref(0)
const localForm = ref({ ...props.reviewForm })
const errors = ref({
  text: {
    isVisible: false,
    message: t('review.form.textareaError'),
  },
  rating: {
    isVisible: false,
    message: t('review.form.starsError'),
  },
  event: {
    isVisible: false,
    message: t('review.form.eventError'),
  },
})

const getTranslationByCategory = (key) => {
  const category = props.item?.data?.specialization?.toLowerCase()
  const message = t(`review.form.${category}.${key}`)

  if (category && message) {
    return message
  }

  return t(`review.form.${key}`)
}

const {
  uploadedFiles,
  isUploading,
  handleFileUpload: _handleFileUpload,
  removeFile: _removeFile,
  getFileUrl,
  // hasExceededLimit,
  getUploadedMedia,
} = useFileUpload()

const handleFileUpload = async (event: Event) => {
  await _handleFileUpload(event, 5).catch(() => {
    toast(t('review.notification.error.title'), {
      autoClose: 3000,
      type: 'error',
      position: toast.POSITION.TOP_CENTER,
    } as ToastOptions)
  })
  updateMedia()
}

const removeFile = (index: number) => {
  _removeFile(index)
  updateMedia()
}

const validateForm = () => {
  let isErrors = false

  // reset errors
  Object.keys(errors.value).forEach((key) => {
    errors.value[key].isVisible = false
  })

  // check if event is selected
  if (
    props.allowChangingEvent &&
    !props.item?.data?.event_name &&
    !props.item?.data?.title
  ) {
    isErrors = true
    errors.value.event.isVisible = true
  }

  // check if review is empty
  if (!localForm.value.review || localForm.value.review === '') {
    isErrors = true
    errors.value.text.isVisible = true
  }

  // check if rating is empty
  if (!localForm.value.rating || localForm.value.rating === 0) {
    isErrors = true
    errors.value.rating.isVisible = true
  }

  return isErrors
}

const submit = () => {
  if (validateForm()) return
  emit('submit')
}

const updateMedia = () => {
  const media = getUploadedMedia()
  localForm.value = {
    ...localForm.value,
    media,
  }
  emit('update:reviewForm', localForm.value)
}

const setRating = (value: number) => {
  localForm.value = {
    ...localForm.value,
    rating: value,
  }
  emit('update:reviewForm', localForm.value)
}

const updateReview = () => {
  emit('update:reviewForm', localForm.value)
}

watch(
  () => localForm.value.review,
  () => {
    updateReview()
    validateForm()
  }
)

watch(
  () => localForm.value.rating,
  () => {
    validateForm()
  }
)

watch(
  () => props.reviewForm,
  (newValue) => {
    localForm.value = { ...newValue }
  },
  { deep: true }
)

watch(
  () => props.item?.data?.title,
  () => validateForm()
)
</script>

<style scoped lang="postcss">
.step {
  @apply max-w-[600px] m-auto;
  animation: blockFadeInUp 0.5s ease-in-out forwards;
}

@keyframes blockFadeInUp {
  from {
    opacity: 0;
    visibility: hidden;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    visibility: visible;
    transform: translateY(0);
  }
}

.review-header {
  @apply text-center pt-2.5 pb-2 mb-2.5 flex flex-col items-center;
}

.card {
  @apply bg-white p-4 rounded-lg;
  box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
}

.review-event {
  @apply flex gap-2.5;

  &__image {
    @apply min-w-[110px] w-[110px] h-[110px] bg-gray-300 rounded-lg;

    img {
      @apply w-full h-full object-cover rounded-lg;
    }
  }

  &__title {
    @apply mb-1.5 text-base font-semibold;
  }

  &__date {
    @apply text-sm text-gray-600;
  }
}

.review-form {
  &__stars {
    @apply mb-4;

    .label {
      @apply text-sm mb-2 text-gray-600;
    }

    .stars {
      @apply flex gap-1.5;
    }

    .star {
      @apply relative w-[27px] h-[24px] cursor-pointer;

      .icon {
        @apply absolute left-0 top-0 text-gray-300;
      }

      .full {
        @apply absolute left-[-1px] top-0 opacity-0 transition-opacity duration-200;
        color: #ffb800;

        &.active {
          @apply opacity-100;
        }
      }
    }
  }

  &__title {
    @apply text-base font-medium mb-2;
  }

  &__text {
    textarea {
      @apply rounded-lg border border-stone-200 w-full p-3 text-sm;
      min-height: 128px;
      resize: vertical;
    }
  }

  &__notification {
    @apply text-xs mt-2 text-gray-500 font-normal;
  }

  &__legal {
    @apply text-xs mt-2 text-gray-500;

    a {
      @apply text-primary hover:underline;
    }
  }
}

.upload-section {
  @apply flex flex-wrap gap-2;

  &__item {
    @apply relative w-[100px] h-[100px] rounded-lg flex items-center justify-center bg-gray-50 cursor-pointer;

    &.uploading {
      @apply opacity-50 cursor-not-allowed;
    }

    input {
      @apply hidden;
    }

    .icon {
      @apply w-[16px] h-[16px] flex items-center justify-center z-1;
    }

    .loader {
      @apply w-6 h-6 border-2 border-gray-300 border-t-primary rounded-full;
      animation: spin 1s linear infinite;
    }
  }

  &__preview {
    @apply overflow-hidden;

    img,
    video {
      @apply w-full h-full object-cover;
    }
  }

  &__remove {
    @apply absolute top-1 right-1 w-5 h-5 rounded-full bg-white flex items-center justify-center
    text-gray-600 hover:text-gray-900 shadow-sm;
  }
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}
</style>
